import { useEffect, useState } from 'react'

/**
 * The single breakpoint source.
 *
 * CLAUDE.md: no component may read `window.innerWidth` or declare its own
 * layout media query. `single-source.test.ts` enforces that across both the
 * TypeScript and the stylesheets — a media query in a component's CSS is the
 * same drift as one in its JSX, and it is where #90's fix must NOT go.
 *
 * Boundaries from master PRD §6.
 */

export type Breakpoint = 'phone' | 'tablet' | 'desktop' | 'wide'

export const BREAKPOINTS = { tablet: 640, desktop: 1024, wide: 1440 } as const

/**
 * Ordered widest-first and mutually exclusive, so the first match is the
 * answer. Written as explicit ranges rather than open-ended `min-width` alone:
 * overlapping queries would make the result depend on iteration order, which is
 * the kind of thing that holds until someone reorders the array.
 */
const QUERIES: Array<[Breakpoint, string]> = [
  ['wide', `(min-width: ${BREAKPOINTS.wide}px)`],
  ['desktop', `(min-width: ${BREAKPOINTS.desktop}px) and (max-width: ${BREAKPOINTS.wide - 1}px)`],
  ['tablet', `(min-width: ${BREAKPOINTS.tablet}px) and (max-width: ${BREAKPOINTS.desktop - 1}px)`],
  ['phone', `(max-width: ${BREAKPOINTS.tablet - 1}px)`],
]

const COARSE = '(pointer: coarse)'

function resolve(): Breakpoint {
  for (const [name, q] of QUERIES) if (window.matchMedia(q).matches) return name
  // Unreachable: the four ranges are exhaustive over [0, ∞), which
  // `the four ranges are exhaustive and do not overlap` pins. Present because
  // the loop must return something, not as a behavioural default.
  return 'desktop'
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(resolve)

  useEffect(() => {
    // The SAME MediaQueryList objects are subscribed and unsubscribed —
    // `window.matchMedia(q)` returns a fresh object per call, so building the
    // list twice would remove handlers from lists nothing is listening to and
    // leak the ones that are.
    const mqls = QUERIES.map(([, q]) => window.matchMedia(q))
    const onChange = () => setBp(resolve())
    mqls.forEach((m) => m.addEventListener('change', onChange))
    return () => mqls.forEach((m) => m.removeEventListener('change', onChange))
  }, [])

  return bp
}

export function useIsCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(() => window.matchMedia(COARSE).matches)

  useEffect(() => {
    const m = window.matchMedia(COARSE)
    // Reads from `m`, not from a captured boolean: the event fires without a
    // payload, so re-reading the list is the only way to get the new value.
    const onChange = () => setCoarse(m.matches)
    m.addEventListener('change', onChange)
    return () => m.removeEventListener('change', onChange)
  }, [])

  return coarse
}
